
'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';

interface ImageLightboxProps {
  images: string[];
  startIndex: number;
  onClose: () => void;
}

export function ImageLightbox({ images, startIndex, onClose }: ImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToNext, goToPrevious, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in-0">
      <div className="relative w-full h-full flex items-center justify-center">
        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 z-10 text-white hover:text-white hover:bg-white/20 rounded-full h-12 w-12"
          onClick={onClose}
        >
          <X className="h-8 w-8" />
        </Button>

        {/* Previous Button */}
        {images.length > 1 && (
            <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:text-white hover:bg-white/20 rounded-full h-14 w-14"
            onClick={goToPrevious}
            >
            <ChevronLeft className="h-10 w-10" />
            </Button>
        )}

        {/* Image Display */}
        <div className="relative w-full max-w-4xl h-full max-h-[90vh] flex items-center justify-center">
           {images.map((src, index) => (
             <div
                key={src}
                className={cn(
                    "absolute w-full h-full transition-opacity duration-300 ease-in-out",
                    index === currentIndex ? "opacity-100" : "opacity-0"
                )}
             >
                <Image
                    src={src}
                    alt={`Image ${index + 1}`}
                    fill
                    className="object-contain"
                />
             </div>
           ))}
        </div>

        {/* Next Button */}
        {images.length > 1 && (
            <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:text-white hover:bg-white/20 rounded-full h-14 w-14"
            onClick={goToNext}
            >
            <ChevronRight className="h-10 w-10" />
            </Button>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 rounded-full px-3 py-1 text-sm">
                {currentIndex + 1} / {images.length}
            </div>
        )}
      </div>
    </div>
  );
}
